import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Plus, RefreshCcw, Search, Trash2, UserPlus, Users, Layers, Archive, RotateCcw, Upload, FileText, CheckCircle2, AlertCircle } from "lucide-react";
import { Header } from "@/components/neusim/Header";
import { AdminNav } from "@/components/neusim/AdminNav";
import { CreateCohortModal } from "@/components/admin/CreateCohortModal";
import { apiCall, ApiError } from "@/lib/api";
import { useSimulations } from "@/hooks/useSimulations";

interface CohortListRow {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  project: { id: string; name: string };
  client: { id: string; name: string };
  memberCount: number;
  activeMemberCount: number;
  simCount: number;
  activeSimCount: number;
}

interface CohortDetailMember {
  userId: string;
  fullName: string;
  email: string;
  role: string;
  isActive: boolean;
  joinedAt: string;
}

interface CohortDetailSim {
  simulationId: string;
  simCode: string;
  title: string;
  competencyId: string | null;
  competencyName: string | null;
  isActive: boolean;
  assignedAt: string;
}

interface CohortDetailResponse {
  cohort: {
    id: string;
    name: string;
    slug: string | null;
    isActive: boolean;
    createdAt: string;
    project: { id: string; name: string };
    client: { id: string; name: string };
  };
  members: CohortDetailMember[];
  sims: CohortDetailSim[];
}

interface ProjectOption { id: string; name: string; clientId: string; clientName: string }
interface UserPicklist { id: string; fullName: string; email: string; role: string }
interface SimPicklist { id: string; simCode: string; title: string; competencyName: string | null }

export function AdminCohortsPage(): JSX.Element {
  const navigate = useNavigate();
  const { role } = useSimulations();
  const canWrite = role === "super_admin";

  const [cohorts, setCohorts] = useState<CohortListRow[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [showArchived, setShowArchived] = useState(false);
  const [clientFilter, setClientFilter] = useState<string>("");
  const [projectFilter, setProjectFilter] = useState<string>("");
  const [search, setSearch] = useState("");

  const [activeCohortId, setActiveCohortId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CohortDetailResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);

  const loadCohorts = useCallback(async (): Promise<void> => {
    setLoadingList(true);
    setListError(null);
    try {
      const res = await apiCall<{ cohorts: CohortListRow[] }>("/libraryCohortList", {
        method: "POST",
        body: { includeArchived: showArchived },
        timeoutMs: 120_000,
      });
      setCohorts(res.cohorts);
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed");
    } finally { setLoadingList(false); }
  }, [showArchived]);

  useEffect(() => { void loadCohorts(); }, [loadCohorts]);

  const loadDetail = useCallback(async (cohortId: string): Promise<void> => {
    setLoadingDetail(true);
    setDetailError(null);
    try {
      const res = await apiCall<CohortDetailResponse>("/libraryCohortDetailNew", {
        method: "POST", body: { cohortId }, timeoutMs: 120_000,
      });
      setDetail(res);
    } catch (err) {
      setDetailError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed");
    } finally { setLoadingDetail(false); }
  }, []);

  useEffect(() => {
    if (activeCohortId !== null) void loadDetail(activeCohortId);
  }, [activeCohortId, loadDetail]);

  const clients = useMemo(() => {
    const map = new Map<string, string>();
    cohorts.forEach((c) => map.set(c.client.id, c.client.name));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [cohorts]);

  const projects = useMemo(() => {
    const map = new Map<string, { id: string; name: string; clientId: string }>();
    cohorts.forEach((c) => {
      if (clientFilter !== "" && c.client.id !== clientFilter) return;
      map.set(c.project.id, { id: c.project.id, name: c.project.name, clientId: c.client.id });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [cohorts, clientFilter]);

  const visible = useMemo(() => {
    return cohorts.filter((c) => {
      if (clientFilter !== "" && c.client.id !== clientFilter) return false;
      if (projectFilter !== "" && c.project.id !== projectFilter) return false;
      if (search.trim() !== "") {
        const q = search.toLowerCase();
        if (!c.name.toLowerCase().includes(q) && !c.project.name.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [cohorts, clientFilter, projectFilter, search]);

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-neusim-bg font-sans text-white">
      <div aria-hidden className="pointer-events-none absolute -left-40 -top-40 h-[700px] w-[700px] rounded-full" style={{ background: "#a855f7", opacity: 0.16, filter: "blur(180px)" }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -right-40 h-[700px] w-[700px] rounded-full" style={{ background: "#22d3ee", opacity: 0.12, filter: "blur(180px)" }} />
      <Header />

      <main className="relative mx-auto max-w-[1200px] px-6 pb-16 pt-24">
        <AdminNav extraTrailing={
          <>
            <button type="button" onClick={() => void loadCohorts()} className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/75 hover:bg-white/10" title="Reload">
              <RefreshCcw className="h-3.5 w-3.5" />
            </button>
            {canWrite && (
              <button type="button" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/15 px-3 py-1.5 text-xs font-semibold text-fuchsia-100 hover:bg-fuchsia-500/25">
                <Plus className="h-3.5 w-3.5" /> New cohort
              </button>
            )}
          </>
        } />
        <div className="mb-6 mt-6">
          <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-fuchsia-300">Cohorts</span>
          <h1 className="mt-0.5 text-2xl font-bold">Manage cohorts</h1>
          <p className="text-xs text-white/55">Cohort = a group of learners running one or more sims under a project.</p>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or project"
                   className="rounded-lg border border-white/10 bg-white/5 pl-9 pr-3 py-2 text-sm text-white placeholder-white/40 outline-none focus:border-white/30" />
          </div>
          <select value={clientFilter} onChange={(e) => { setClientFilter(e.target.value); setProjectFilter(""); }}
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-white/30">
            <option value="" className="bg-neusim-bg">All clients</option>
            {clients.map((c) => (<option key={c.id} value={c.id} className="bg-neusim-bg">{c.name}</option>))}
          </select>
          <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-white/30" disabled={projects.length === 0}>
            <option value="" className="bg-neusim-bg">All projects</option>
            {projects.map((p) => (<option key={p.id} value={p.id} className="bg-neusim-bg">{p.name}</option>))}
          </select>
          <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs text-white/70">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-fuchsia-400" />
            Show archived
          </label>
        </div>

        {loadingList && <div className="rounded-2xl border border-white/10 bg-white/5 px-6 py-12 text-center text-sm text-white/60">Loading cohorts…</div>}
        {listError !== null && <div className="rounded-2xl border border-red-400/30 bg-red-500/10 px-6 py-6 text-sm text-red-200">{listError}</div>}
        {!loadingList && listError === null && visible.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/5 px-6 py-12 text-center text-sm text-white/60">
            No cohorts match these filters. Create a new one to get started.
          </div>
        )}
        {!loadingList && listError === null && visible.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wider text-white/50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Cohort</th>
                  <th className="px-4 py-2 text-left font-medium">Project</th>
                  <th className="px-4 py-2 text-left font-medium">Client</th>
                  <th className="px-4 py-2 text-right font-medium">Members</th>
                  <th className="px-4 py-2 text-right font-medium">Sims</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <tr key={c.id} onClick={() => setActiveCohortId(c.id)}
                      className={`cursor-pointer border-t border-white/5 transition-colors hover:bg-white/5 ${activeCohortId === c.id ? "bg-white/[0.06]" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 font-medium">
                        {c.name}
                        {!c.isActive && <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-white/60">Archived</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/70">{c.project.name}</td>
                    <td className="px-4 py-3 text-white/70">{c.client.name}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">
                      {c.activeMemberCount}{c.memberCount !== c.activeMemberCount && <span className="text-white/30"> / {c.memberCount}</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">
                      {c.activeSimCount}{c.simCount !== c.activeSimCount && <span className="text-white/30"> / {c.simCount}</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-white/30"><ChevronRight className="h-4 w-4" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {activeCohortId !== null && (
        <CohortDetailPanel
          loading={loadingDetail}
          error={detailError}
          detail={detail}
          canWrite={canWrite}
          onClose={() => { setActiveCohortId(null); setDetail(null); }}
          onChanged={() => { void loadDetail(activeCohortId!); void loadCohorts(); }}
        />
      )}
      {showCreate && canWrite && (
        <CreateCohortModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); setActiveCohortId(id); void loadCohorts(); }}
        />
      )}
    </div>
  );
}

// ───────────────────────── Detail panel ─────────────────────────

interface DetailProps {
  loading: boolean;
  error: string | null;
  detail: CohortDetailResponse | null;
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => void;
}

function CohortDetailPanel({ loading, error, detail, canWrite, onClose, onChanged }: DetailProps): JSX.Element {
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddSim, setShowAddSim] = useState(false);
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <div className="fixed inset-0 z-40 flex items-stretch justify-end bg-black/40" onClick={onClose}>
        <div className="h-full w-full max-w-[680px] overflow-y-auto bg-neusim-bg p-6 text-white" onClick={(e) => e.stopPropagation()}>
          <div className="text-sm text-white/60">Loading cohort…</div>
        </div>
      </div>
    );
  }
  if (error !== null) {
    return (
      <div className="fixed inset-0 z-40 flex items-stretch justify-end bg-black/40" onClick={onClose}>
        <div className="h-full w-full max-w-[680px] overflow-y-auto bg-neusim-bg p-6 text-white" onClick={(e) => e.stopPropagation()}>
          <div className="text-sm text-red-300">{error}</div>
        </div>
      </div>
    );
  }
  if (detail === null) return <></>;

  const toggleArchived = async (): Promise<void> => {
    setBusy(true);
    try {
      await apiCall("/libraryCohortSave", { method: "POST", body: { cohortId: detail.cohort.id, isActive: !detail.cohort.isActive }, timeoutMs: 60_000 });
      onChanged();
    } finally { setBusy(false); }
  };

  const removeMember = async (userId: string): Promise<void> => {
    setBusy(true);
    try {
      await apiCall("/libraryCohortMemberSave", { method: "POST", body: { cohortId: detail.cohort.id, action: "remove", userIds: [userId] }, timeoutMs: 60_000 });
      onChanged();
    } finally { setBusy(false); }
  };

  const removeSim = async (simulationId: string): Promise<void> => {
    setBusy(true);
    try {
      await apiCall("/libraryCohortSimSave", { method: "POST", body: { cohortId: detail.cohort.id, action: "remove", simulationIds: [simulationId] }, timeoutMs: 60_000 });
      onChanged();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-stretch justify-end bg-black/40" onClick={onClose}>
      <div className="h-full w-full max-w-[680px] overflow-y-auto bg-neusim-bg p-6 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/40">{detail.cohort.client.name} · {detail.cohort.project.name}</div>
            <h2 className="mt-1 text-xl font-bold">{detail.cohort.name}</h2>
            {!detail.cohort.isActive && <span className="mt-1 inline-block rounded bg-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/60">Archived</span>}
          </div>
          <div className="flex items-center gap-2">
            {canWrite && (
              <button type="button" disabled={busy} onClick={() => void toggleArchived()}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80 transition-colors hover:bg-white/10 disabled:opacity-50">
                {detail.cohort.isActive ? <><Archive className="h-3.5 w-3.5" /> Archive</> : <><RotateCcw className="h-3.5 w-3.5" /> Unarchive</>}
              </button>
            )}
            <button type="button" onClick={onClose} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10">Close</button>
          </div>
        </div>

        {/* Members */}
        <div className="mb-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold"><Users className="h-4 w-4 text-white/60" /> Members</h3>
            {canWrite ? (
              <button type="button" onClick={() => setShowAddMember(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/15 px-3 py-1 text-xs font-medium text-fuchsia-100 hover:bg-fuchsia-500/25">
                <UserPlus className="h-3.5 w-3.5" /> Add
              </button>
            ) : (
              <span className="rounded bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/40">Read only</span>
            )}
          </div>
          {detail.members.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-white/40">No members yet.</div>
          ) : (
            <div className="space-y-1">
              {detail.members.map((m) => (
                <div key={m.userId} className={`flex items-center justify-between rounded-lg px-3 py-2 ${m.isActive ? "bg-white/[0.04]" : "bg-white/[0.015] opacity-50"}`}>
                  <div className="min-w-0">
                    <div className="truncate text-sm">{m.fullName}</div>
                    <div className="truncate text-[11px] text-white/50">{m.email}</div>
                  </div>
                  {m.isActive && canWrite && (
                    <button type="button" disabled={busy} onClick={() => void removeMember(m.userId)} title="Remove from cohort"
                            className="rounded-md p-1.5 text-white/40 transition-colors hover:bg-red-500/20 hover:text-red-300">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sims */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold"><Layers className="h-4 w-4 text-white/60" /> Simulations</h3>
            {canWrite ? (
              <button type="button" onClick={() => setShowAddSim(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/15 px-3 py-1 text-xs font-medium text-fuchsia-100 hover:bg-fuchsia-500/25">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            ) : (
              <span className="rounded bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/40">Read only</span>
            )}
          </div>
          {detail.sims.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-white/40">No simulations assigned to this cohort yet.</div>
          ) : (
            <div className="space-y-1">
              {detail.sims.map((s) => (
                <div key={s.simulationId} className={`flex items-center justify-between rounded-lg px-3 py-2 ${s.isActive ? "bg-white/[0.04]" : "bg-white/[0.015] opacity-50"}`}>
                  <div className="min-w-0">
                    <div className="truncate text-sm">{s.title}</div>
                    <div className="truncate text-[11px] text-white/50">{s.simCode}{s.competencyName !== null ? ` · ${s.competencyName}` : ""}</div>
                  </div>
                  {s.isActive && canWrite && (
                    <button type="button" disabled={busy} onClick={() => void removeSim(s.simulationId)} title="Remove sim from cohort"
                            className="rounded-md p-1.5 text-white/40 transition-colors hover:bg-red-500/20 hover:text-red-300">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {showAddMember && (
          <AddMembersModal cohortId={detail.cohort.id} existingUserIds={new Set(detail.members.filter((m) => m.isActive).map((m) => m.userId))}
                           onClose={() => setShowAddMember(false)} onAdded={() => { setShowAddMember(false); onChanged(); }} />
        )}
        {showAddSim && (
          <AddSimsModal cohortId={detail.cohort.id} existingSimIds={new Set(detail.sims.filter((s) => s.isActive).map((s) => s.simulationId))}
                       onClose={() => setShowAddSim(false)} onAdded={() => { setShowAddSim(false); onChanged(); }} />
        )}
      </div>
    </div>
  );
}

// (CreateCohortModal was extracted to src/components/admin/CreateCohortModal.tsx
//  so AdminClientsPage can spawn the same flow from inside a client expansion.)

// ───────────────────────── Add members modal ─────────────────────────

interface AddMembersProps { cohortId: string; existingUserIds: Set<string>; onClose: () => void; onAdded: () => void }

type AddMembersTab = "pick" | "csv";

function AddMembersModal({ cohortId, existingUserIds, onClose, onAdded }: AddMembersProps): JSX.Element {
  const [tab, setTab] = useState<AddMembersTab>("pick");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="flex h-[80vh] w-full max-w-2xl flex-col rounded-2xl border border-white/10 bg-neusim-bg p-6 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold">Add members</h3>
            <p className="text-xs text-white/60">Pick learners to add to this cohort. They'll see every active sim attached to the cohort.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-white/60 hover:bg-white/10">×</button>
        </div>

        {/* Tab strip */}
        <div className="mb-4 inline-flex w-fit rounded-xl border border-white/10 bg-white/[0.03] p-1">
          <button type="button" onClick={() => setTab("pick")}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${tab === "pick" ? "bg-fuchsia-500/20 text-fuchsia-100" : "text-white/60 hover:bg-white/5"}`}>
            <Users className="h-3.5 w-3.5" /> Pick existing
          </button>
          <button type="button" onClick={() => setTab("csv")}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${tab === "csv" ? "bg-fuchsia-500/20 text-fuchsia-100" : "text-white/60 hover:bg-white/5"}`}>
            <Upload className="h-3.5 w-3.5" /> Paste CSV
          </button>
        </div>

        {tab === "pick"
          ? <PickExistingTab cohortId={cohortId} existingUserIds={existingUserIds} onClose={onClose} onAdded={onAdded} />
          : <PasteCsvTab    cohortId={cohortId}                                    onClose={onClose} onAdded={onAdded} />}
      </div>
    </div>
  );
}

// — Pick existing tab (the original UX, lifted as-is) —
function PickExistingTab({ cohortId, existingUserIds, onClose, onAdded }: AddMembersProps): JSX.Element {
  const [users, setUsers] = useState<UserPicklist[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async (): Promise<void> => {
      try {
        const res = await apiCall<{ users: Array<{ id: string; fullName: string; email: string; role: string }> }>("/libraryCohortPickUsers", { method: "POST", body: { cohortId }, timeoutMs: 120_000 });
        if (alive) setUsers(res.users);
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : "Failed to load users");
      }
    })();
    return () => { alive = false; };
  }, [cohortId]);

  const visible = useMemo(() => {
    const q = search.toLowerCase().trim();
    return users.filter((u) =>
      !existingUserIds.has(u.id) &&
      (q === "" || u.fullName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    );
  }, [users, existingUserIds, search]);

  const submit = async (): Promise<void> => {
    if (picked.size === 0) return;
    setSaving(true);
    setError(null);
    try {
      await apiCall("/libraryCohortMemberSave", { method: "POST", body: { cohortId, action: "add", userIds: Array.from(picked) }, timeoutMs: 60_000 });
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <>
      <input type="text" placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)}
             className="mb-3 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-white/30" />
      <div className="mb-3 flex-1 overflow-y-auto rounded-xl border border-white/10">
        {visible.length === 0 && <div className="px-4 py-6 text-center text-xs text-white/40">No matching learners.</div>}
        {visible.map((u) => (
          <label key={u.id} className="flex cursor-pointer items-center gap-3 border-b border-white/5 px-3 py-2 hover:bg-white/5">
            <input type="checkbox" checked={picked.has(u.id)} onChange={(e) => {
              setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(u.id); else n.delete(u.id); return n; });
            }} className="accent-fuchsia-400" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm">{u.fullName}</div>
              <div className="truncate text-[11px] text-white/50">{u.email}</div>
            </div>
          </label>
        ))}
      </div>
      {error !== null && <div className="mb-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</div>}
      <div className="flex items-center justify-between">
        <div className="text-xs text-white/60">{picked.size} selected</div>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 hover:bg-white/10">Cancel</button>
          <button type="button" disabled={saving || picked.size === 0} onClick={() => void submit()}
                  className="rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/20 px-4 py-2 text-sm font-semibold text-fuchsia-100 hover:bg-fuchsia-500/30 disabled:opacity-50">
            {saving ? "Adding…" : `Add ${picked.size}`}
          </button>
        </div>
      </div>
    </>
  );
}

// — Paste CSV tab —

interface CsvRow { fullName: string; email: string; error?: string }
interface CsvResultRow {
  rowIndex: number;
  email: string;
  fullName: string;
  status: "created" | "added_existing" | "already_member" | "skipped";
  reason?: string;
  inviteStatus?: "sent" | "created_silent" | "skipped" | "failed" | "not_attempted";
  inviteReason?: string;
}
interface CsvSummary { created: number; addedExisting: number; alreadyMember: number; skipped: number }

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Parse pasted CSV. Accepts:
 *   - one row per line: "Full Name, email@x.com"  (comma or tab delimited)
 *   - optional header row "name,email" or "fullName,email" (case-insensitive, dropped)
 *   - blank lines skipped
 * Per-row error captured (Invalid email, Missing name) — surfaced in the preview.
 */
function parseCsv(text: string): CsvRow[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l !== "");
  if (lines.length === 0) return [];
  const out: CsvRow[] = [];
  let start = 0;
  // Skip header
  const first = (lines[0] ?? "").toLowerCase();
  if ((first.includes("name") || first.includes("full")) && first.includes("email")) start = 1;

  for (let i = start; i < lines.length; i++) {
    const line = lines[i] ?? "";
    const parts = line.split(/\t|,/).map((p) => p.trim().replace(/^["']|["']$/g, ""));
    const fullName = parts[0] ?? "";
    const email = (parts[1] ?? "").toLowerCase();
    let err: string | undefined;
    if (email === "" || !EMAIL_RE.test(email)) err = "Invalid email";
    else if (fullName === "") err = "Missing name";
    out.push({ fullName, email, error: err });
  }
  return out;
}

function statusPill(status: CsvResultRow["status"]): { label: string; cls: string } {
  switch (status) {
    case "created":        return { label: "Created",       cls: "border-emerald-400/40 bg-emerald-500/10 text-emerald-200" };
    case "added_existing": return { label: "Added",         cls: "border-sky-400/40 bg-sky-500/10 text-sky-200" };
    case "already_member": return { label: "Already in",    cls: "border-white/15 bg-white/5 text-white/60" };
    case "skipped":        return { label: "Skipped",       cls: "border-red-400/40 bg-red-500/10 text-red-200" };
  }
}

function invitePill(status: CsvResultRow["inviteStatus"]): { label: string; cls: string } | null {
  switch (status) {
    case "sent":           return { label: "Invite sent",   cls: "border-emerald-400/40 bg-emerald-500/10 text-emerald-200" };
    case "created_silent": return { label: "Guest created", cls: "border-sky-400/40 bg-sky-500/10 text-sky-200" };
    case "skipped":        return { label: "Already guest", cls: "border-white/15 bg-white/5 text-white/60" };
    case "failed":         return { label: "Invite failed", cls: "border-amber-400/40 bg-amber-500/10 text-amber-200" };
    case "not_attempted":  return { label: "Not invited",   cls: "border-white/10 bg-white/5 text-white/50" };
    default:               return null;
  }
}

function PasteCsvTab({ cohortId, onClose, onAdded }: { cohortId: string; onClose: () => void; onAdded: () => void }): JSX.Element {
  const [text, setText] = useState("");
  const [sendInvites, setSendInvites] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ summary: CsvSummary; rows: CsvResultRow[] } | null>(null);

  const parsed = useMemo(() => parseCsv(text), [text]);
  const validCount = parsed.filter((r) => r.error === undefined).length;
  const invalidCount = parsed.length - validCount;

  const onFile = (file: File | null): void => {
    if (file === null) return;
    const reader = new FileReader();
    reader.onload = (): void => { setText(String(reader.result ?? "")); };
    reader.readAsText(file);
  };

  const submit = async (): Promise<void> => {
    if (validCount === 0) return;
    setSaving(true);
    setError(null);
    try {
      const rows = parsed.filter((r) => r.error === undefined).map((r) => ({ fullName: r.fullName, email: r.email }));
      const res = await apiCall<{ ok: true; summary: CsvSummary; rows: CsvResultRow[] }>(
        "/libraryCohortBulkAddMembers", { method: "POST", body: { cohortId, rows, sendInvites }, timeoutMs: 120_000 },
      );
      setResult({ summary: res.summary, rows: res.rows });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Bulk upload failed");
    } finally { setSaving(false); }
  };

  // Result view (after submit)
  if (result !== null) {
    const { summary, rows } = result;
    const total = summary.created + summary.addedExisting + summary.alreadyMember + summary.skipped;
    return (
      <>
        <div className="mb-4 grid grid-cols-4 gap-2">
          <SummaryTile label="Created"     value={summary.created}       tone="emerald" />
          <SummaryTile label="Added"       value={summary.addedExisting} tone="sky" />
          <SummaryTile label="Already in"  value={summary.alreadyMember} tone="quiet" />
          <SummaryTile label="Skipped"     value={summary.skipped}       tone="red" />
        </div>
        <div className="mb-3 flex-1 overflow-y-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-neusim-bg">
              <tr className="border-b border-white/10 text-[10px] uppercase tracking-[0.14em] text-white/45">
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Invite</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const p = statusPill(r.status);
                const inv = invitePill(r.inviteStatus);
                return (
                  <tr key={r.rowIndex} className="border-b border-white/5">
                    <td className="px-3 py-2 text-white/40">{r.rowIndex + 1}</td>
                    <td className="px-3 py-2 text-white/80">{r.fullName || "—"}</td>
                    <td className="px-3 py-2 text-white/60">{r.email || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] ${p.cls}`}>{p.label}</span>
                      {r.reason !== undefined && <div className="mt-0.5 text-[10px] text-white/40">{r.reason}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {inv === null ? <span className="text-white/30">—</span> : (
                        <>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] ${inv.cls}`}>{inv.label}</span>
                          {r.inviteReason !== undefined && <div className="mt-0.5 text-[10px] text-white/40">{r.inviteReason}</div>}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs text-white/60">{total} rows processed</div>
          <div className="flex gap-2">
            <button type="button" onClick={() => { setResult(null); setText(""); }} className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 hover:bg-white/10">
              Upload more
            </button>
            <button type="button" onClick={onAdded} className="rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/20 px-4 py-2 text-sm font-semibold text-fuchsia-100 hover:bg-fuchsia-500/30">
              Done
            </button>
          </div>
        </div>
      </>
    );
  }

  // Compose view (paste / upload)
  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] text-white/55">
          Format: <span className="font-mono text-white/75">Full Name, email@example.com</span> — one learner per line. Header row optional.
        </div>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80 hover:bg-white/10">
          <FileText className="h-3.5 w-3.5" />
          Upload .csv
          <input type="file" accept=".csv,text/csv,text/plain" className="hidden"
                 onChange={(e) => { onFile(e.target.files?.[0] ?? null); e.target.value = ""; }} />
        </label>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={`Alex Patel, alex.patel@accenture.example\nPriya Singh, priya.singh@accenture.example`}
                className="mb-3 h-40 w-full resize-none rounded-xl border border-white/10 bg-white/5 px-3 py-2 font-mono text-xs text-white outline-none focus:border-white/30" />

      {parsed.length > 0 && (
        <div className="mb-3 flex-1 overflow-y-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-neusim-bg">
              <tr className="border-b border-white/10 text-[10px] uppercase tracking-[0.14em] text-white/45">
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Check</th>
              </tr>
            </thead>
            <tbody>
              {parsed.map((r, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="px-3 py-2 text-white/40">{i + 1}</td>
                  <td className="px-3 py-2 text-white/80">{r.fullName || "—"}</td>
                  <td className="px-3 py-2 text-white/60">{r.email || "—"}</td>
                  <td className="px-3 py-2">
                    {r.error === undefined
                      ? <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 className="h-3 w-3" /> OK</span>
                      : <span className="inline-flex items-center gap-1 text-red-300"><AlertCircle className="h-3 w-3" /> {r.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {parsed.length === 0 && <div className="mb-3 flex-1" />}

      {error !== null && <div className="mb-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</div>}

      <label className="mb-3 flex cursor-pointer items-start gap-2 text-xs text-white/75">
        <input type="checkbox" checked={sendInvites} onChange={(e) => setSendInvites(e.target.checked)} className="mt-0.5 accent-fuchsia-400" />
        <span>
          <span className="font-semibold">Send invitation email</span> to newly created learners.
          <span className="block text-[11px] text-white/45">Either way the learner is added as an Entra guest so they can log in. Uncheck if your future custom-invite-emails module will handle the email.</span>
        </span>
      </label>

      <div className="flex items-center justify-between">
        <div className="text-xs text-white/60">
          {parsed.length === 0
            ? <>Paste CSV above to preview.</>
            : <>{validCount} ready{invalidCount > 0 && <span className="text-red-300"> · {invalidCount} invalid</span>}</>}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 hover:bg-white/10">Cancel</button>
          <button type="button" disabled={saving || validCount === 0} onClick={() => void submit()}
                  className="rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/20 px-4 py-2 text-sm font-semibold text-fuchsia-100 hover:bg-fuchsia-500/30 disabled:opacity-50">
            {saving ? "Uploading…" : `Add ${validCount}`}
          </button>
        </div>
      </div>
    </>
  );
}

function SummaryTile({ label, value, tone }: { label: string; value: number; tone: "emerald" | "sky" | "quiet" | "red" }): JSX.Element {
  const toneCls: Record<typeof tone, string> = {
    emerald: "border-emerald-400/30 bg-emerald-500/10 text-emerald-100",
    sky:     "border-sky-400/30 bg-sky-500/10 text-sky-100",
    quiet:   "border-white/10 bg-white/5 text-white/70",
    red:     "border-red-400/30 bg-red-500/10 text-red-100",
  };
  return (
    <div className={`rounded-xl border px-3 py-2 ${toneCls[tone]}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] opacity-70">{label}</div>
      <div className="mt-0.5 text-lg font-bold">{value}</div>
    </div>
  );
}

// ───────────────────────── Add sims modal ─────────────────────────

interface AddSimsProps { cohortId: string; existingSimIds: Set<string>; onClose: () => void; onAdded: () => void }
function AddSimsModal({ cohortId, existingSimIds, onClose, onAdded }: AddSimsProps): JSX.Element {
  const [sims, setSims] = useState<SimPicklist[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async (): Promise<void> => {
      try {
        // Purpose-built picker endpoint — scoped to the cohort's project.
        // The generic /adminSimList was GET (we were POSTing → 404) + super_admin
        // only; this one accepts client_admin via userHasCohortAccess.
        const res = await apiCall<{ sims: Array<{ id: string; simCode: string; title: string; competencyName: string | null }> }>("/libraryCohortPickSims", { method: "POST", body: { cohortId }, timeoutMs: 120_000 });
        if (alive) setSims(res.sims);
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : "Failed to load sims");
      }
    })();
    return () => { alive = false; };
  }, [cohortId]);

  const visible = useMemo(() => {
    const q = search.toLowerCase().trim();
    return sims.filter((s) =>
      !existingSimIds.has(s.id) &&
      (q === "" || s.title.toLowerCase().includes(q) || s.simCode.toLowerCase().includes(q))
    );
  }, [sims, existingSimIds, search]);

  const submit = async (): Promise<void> => {
    if (picked.size === 0) return;
    setSaving(true);
    setError(null);
    try {
      await apiCall("/libraryCohortSimSave", { method: "POST", body: { cohortId, action: "add", simulationIds: Array.from(picked) }, timeoutMs: 60_000 });
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="flex h-[80vh] w-full max-w-lg flex-col rounded-2xl border border-white/10 bg-neusim-bg p-6 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold">Add simulations</h3>
            <p className="text-xs text-white/60">Pick sims to attach to this cohort. Every active member will get them in their library.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-white/60 hover:bg-white/10">×</button>
        </div>
        <input type="text" placeholder="Search by title or sim code" value={search} onChange={(e) => setSearch(e.target.value)}
               className="mb-3 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-white/30" />
        <div className="mb-3 flex-1 overflow-y-auto rounded-xl border border-white/10">
          {visible.length === 0 && <div className="px-4 py-6 text-center text-xs text-white/40">No matching simulations.</div>}
          {visible.map((s) => (
            <label key={s.id} className="flex cursor-pointer items-center gap-3 border-b border-white/5 px-3 py-2 hover:bg-white/5">
              <input type="checkbox" checked={picked.has(s.id)} onChange={(e) => {
                setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(s.id); else n.delete(s.id); return n; });
              }} className="accent-fuchsia-400" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{s.title}</div>
                <div className="truncate text-[11px] text-white/50">{s.simCode}{s.competencyName !== null ? ` · ${s.competencyName}` : ""}</div>
              </div>
            </label>
          ))}
        </div>
        {error !== null && <div className="mb-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</div>}
        <div className="flex items-center justify-between">
          <div className="text-xs text-white/60">{picked.size} selected</div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 hover:bg-white/10">Cancel</button>
            <button type="button" disabled={saving || picked.size === 0} onClick={() => void submit()}
                    className="rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/20 px-4 py-2 text-sm font-semibold text-fuchsia-100 hover:bg-fuchsia-500/30 disabled:opacity-50">
              {saving ? "Adding…" : `Add ${picked.size}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
